import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Star, TrendingUp, TrendingDown, AlertTriangle, RefreshCw, ChevronsUpDown } from "lucide-react";

/**
 * Iter 253s · Feedback NPS Rollup dashboard.
 *
 * Shows the per-member per-parameter matrix for the selected quarter +
 * optional brand/project filter. Highlights members whose overall avg
 * dips below 3.5 (or NPS goes negative) so managers can flag coaching
 * conversations before review time.
 */
export default function FeedbackNPSReport() {
  const { activeWorkspaceId } = useAuth();
  const [quarter, setQuarter] = useState(currentQuarter());
  const [brandId, setBrandId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/brands`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${activeWorkspaceId}/projects`).catch(() => ({ data: [] })),
    ]).then(([b, p]) => {
      setBrands(Array.isArray(b.data) ? b.data : (b.data?.items || []));
      setProjects(Array.isArray(p.data) ? p.data : []);
    });
  }, [activeWorkspaceId]);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ quarter });
      if (brandId) params.set("brand_id", brandId);
      if (projectId) params.set("project_id", projectId);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/feedback/nps-report?${params.toString()}`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load report")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId, quarter, brandId, projectId]);
  useEffect(() => { load(); }, [load]);

  // Collect all unique parameters across rows to build a stable matrix header.
  const allParams = useMemo(() => {
    const set = new Set();
    (data?.rows || []).forEach((r) => Object.keys(r.parameters || {}).forEach((p) => set.add(p)));
    return Array.from(set).sort();
  }, [data]);

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6" data-testid="feedback-nps-report">
      <PageHeader
        eyebrow="Feedback / NPS"
        title="Quarterly rollup"
        subtitle="Per-member per-parameter averages so you can spot coaching opportunities before review time."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={load} disabled={loading} data-testid="nps-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] uppercase text-muted-foreground">Quarter</span>
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="h-8 w-32 rounded-none text-xs" data-testid="nps-quarter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {quarterOptions().map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] uppercase text-muted-foreground">Brand</span>
          <Select value={brandId || "_all"} onValueChange={(v) => setBrandId(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-44 rounded-none text-xs" data-testid="nps-brand"><SelectValue placeholder="All brands" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All brands</SelectItem>
              {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] uppercase text-muted-foreground">Project</span>
          <Select value={projectId || "_all"} onValueChange={(v) => setProjectId(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-44 rounded-none text-xs" data-testid="nps-project"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!data && loading && <div className="mt-8 text-sm text-muted-foreground">Loading…</div>}
      {data && (
        <>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="nps-summary">
            <SummaryCard label="Ratings collected" value={data.total_ratings} icon={<ChevronsUpDown className="w-4 h-4" />} />
            <SummaryCard label="Members rated" value={data.user_count} icon={<TrendingUp className="w-4 h-4" />} />
            <SummaryCard label="Workspace avg" value={data.overall?.avg ?? "—"} suffix="★" tone={data.overall?.avg >= 4 ? "green" : (data.overall?.avg < 3.5 ? "red" : "amber")} />
            <SummaryCard label="Workspace NPS" value={data.overall?.nps ?? "—"} tone={data.overall?.nps > 0 ? "green" : (data.overall?.nps < 0 ? "red" : "amber")} />
          </div>
          {data.coaching_flags > 0 && (
            <div className="mt-3 border border-amber-500 bg-amber-50 text-amber-800 p-3 text-sm flex items-start gap-2" data-testid="nps-coaching-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                <strong>{data.coaching_flags} member{data.coaching_flags === 1 ? "" : "s"} may need coaching</strong> — flagged when their overall average dips below 3.5 or NPS goes negative for {data.quarter}.
              </div>
            </div>
          )}
          {(data.rows || []).length === 0 && (
            <div className="mt-8 border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="nps-empty">
              No ratings collected in {data.quarter}
              {brandId ? " for the selected brand" : ""}
              {projectId ? " for the selected project" : ""}. Send a feedback request to gather data.
            </div>
          )}
          {(data.rows || []).length > 0 && (
            <div className="mt-4 overflow-x-auto border border-border">
              <table className="w-full text-xs" data-testid="nps-table">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left p-2 sticky left-0 bg-muted/40 z-10">Member</th>
                    <th className="text-right p-2">Overall ★</th>
                    <th className="text-right p-2">NPS</th>
                    <th className="text-right p-2">Ratings</th>
                    {allParams.map((p) => (
                      <th key={p} className="text-right p-2 whitespace-nowrap" title={p}>{p}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.user_id} className={`border-t border-border ${r.needs_coaching ? "bg-amber-50" : ""}`} data-testid={`nps-row-${r.user_id}`}>
                      <td className="p-2 sticky left-0 bg-background z-10">
                        <div className="flex items-center gap-2">
                          {r.user_avatar ? <img src={r.user_avatar} alt="" className="w-6 h-6 rounded-full" /> : (
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                              {r.user_name.slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{r.user_name}</div>
                            <div className="text-[9px] text-muted-foreground">
                              {r.sources.client || 0} client · {r.sources.internal || 0} internal
                            </div>
                          </div>
                          {r.needs_coaching && <AlertTriangle className="w-3.5 h-3.5 text-amber-600 ml-1" title="Consider coaching" />}
                        </div>
                      </td>
                      <td className="text-right p-2 tabular-nums">
                        <span className="inline-flex items-center gap-1">
                          {r.overall.avg ?? "—"}
                          {r.overall.avg && <Star className="w-3 h-3 fill-amber-400 text-amber-400" />}
                        </span>
                      </td>
                      <td className={`text-right p-2 tabular-nums font-mono ${r.overall.nps > 0 ? "text-emerald-700" : r.overall.nps < 0 ? "text-rose-700" : ""}`}>
                        {r.overall.nps ?? "—"}
                      </td>
                      <td className="text-right p-2 text-muted-foreground tabular-nums">{r.overall.count}</td>
                      {allParams.map((p) => {
                        const s = r.parameters[p];
                        if (!s) return <td key={p} className="p-2 text-center text-muted-foreground/40">—</td>;
                        return (
                          <td key={p} className="text-right p-2 tabular-nums" title={`${s.count} rating${s.count === 1 ? "" : "s"}`}>
                            <span className={s.avg < 3.5 ? "text-rose-700" : s.avg >= 4.5 ? "text-emerald-700" : ""}>
                              {s.avg}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, suffix, icon, tone }) {
  const toneCls = tone === "green" ? "border-emerald-500" : tone === "red" ? "border-rose-500" : tone === "amber" ? "border-amber-500" : "border-border";
  return (
    <div className={`border ${toneCls} p-3 bg-background`}>
      <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}{suffix ? <span className="text-lg text-muted-foreground ml-1">{suffix}</span> : null}</div>
    </div>
  );
}

function currentQuarter() {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q}-${d.getFullYear()}`;
}
function quarterOptions() {
  const d = new Date();
  const y = d.getFullYear();
  const q = Math.floor(d.getMonth() / 3) + 1;
  const opts = [];
  // Current + last 4 quarters.
  for (let i = 0; i < 5; i++) {
    let qq = q - i;
    let yy = y;
    while (qq <= 0) { qq += 4; yy -= 1; }
    opts.push(`Q${qq}-${yy}`);
  }
  return opts;
}
